import _ from "lodash";
import { ContainerAtom, HorizontalNoteContainer } from "../model/containers";
import {
    Chord,
    Note,
    NoteDuration,
    NoteVelocity,
    Tone
} from "../model/core";

// in terms of notes and ticks.
export type SelectionGeometry = {
    start: number,
    top: number,
    left: number,
    width: number,
    height: number
};

export class NoteSelectionGroup {
    private notes: ContainerAtom<Note>[] = [];

    constructor(
        private hnc: HorizontalNoteContainer,
        private geometry: SelectionGeometry,
        notes? : ContainerAtom<Note>[]
    ) {
        if (notes) this.notes = [...notes];
    }
    addNote(
        step: number,
        note: Note,
        duration: NoteDuration,
        velocity: NoteVelocity
    ) {
        this.notes.push({ step, payload: note, duration, velocity });
    }
    removeNote(start: number, note: Note) {
        this.notes = this.notes.filter(n => n.step !== start || n.payload !== note);
    }
    getNotes(): ContainerAtom<Note>[] {
        return this.notes;
    }
    clear() {
        this.notes = [];
    }
    contains(start: number, note: Note): boolean {
        return this.notes.some(n => n.step === start && n.payload === note);
    }

    isWithinArea(start: number, note: Note): boolean {
        const { top, left, width, height } = this.geometry;
        return start >= left
            && start < left + width
            && note.index <= top
            && note.index > top - height;
    }

    getGeometry() : SelectionGeometry {
        return this.geometry;
    }

    getContainer() : HorizontalNoteContainer {
        return this.hnc;
    }

    move(
        f: (start: number) => number,
        g: (note: number) => number,
        updateHNC?: (hnc: HorizontalNoteContainer) => void
    ) {
        const newNotes = this.notes.map(n => {
            return {
                step: f(n.step),
                payload: Note.fromIndex(g(n.payload.index)),
                duration: n.duration,
                velocity: n.velocity
            };
        });

        this.notes.forEach(({ step, payload, duration, velocity }, i) => {
            if (this.hnc.get(step).filter(n => n.payload.index === payload.index).length === 0) {
                throw Error(`note not found in HNC: ${step}, ${JSON.stringify(payload)}`);
            }
            this.hnc.remove(step, {
                step: step,
                payload: payload as Note & Chord,
                duration: duration,
                velocity: velocity
            });
        });

        newNotes.forEach((n, i) => {
            this.hnc.put(n.step, {
                step: n.step,
                payload: n.payload as Note & Chord,
                duration: n.duration,
                velocity: n.velocity
            });
        });

        this.notes = newNotes;
        if (updateHNC) updateHNC(this.hnc);
    }

    getUniqueNotes(): Note[] {
        return _.uniq(this.notes.map(n => n.payload.index)).map(Note.fromIndex);
    }
}
